<template>
  <div class="course-files-container">
    <div class="card files-card">
      <div class="card-header">
        <h3>Course Files</h3>
        <button @click="openUploadModal" class="add-file-btn">
          <i class="fas fa-plus"></i> Add File
        </button>
      </div>
      
      <div class="card-content">
        <div v-if="loading" class="loading-container">
          <div class="spinner"></div>
          <p>Loading files...</p>
        </div>
        
        <div v-else-if="files.length === 0" class="empty-state">
          <i class="fas fa-file-alt empty-icon"></i>
          <p>No files have been added to this course yet.</p>
          <button @click="openUploadModal" class="add-first-file-btn">
            Add your first file
          </button>
        </div>
        
        <div v-else class="files-list">
          <div v-for="file in files" :key="file.id" class="file-item">
            <div class="file-icon">
              <i class="fas fa-file"></i>
            </div>
            <div class="file-details">
              <h4 class="file-title">{{ file.title }}</h4>
              <p class="file-date">Added: {{ formatDate(file.created_at) }}</p>
            </div>
            <div class="file-actions">
              <a :href="getFileUrl(file)" target="_blank" class="file-action-btn view-btn">
                <i class="fas fa-eye"></i>
              </a>
              <button @click="confirmDeleteFile(file)" class="file-action-btn delete-btn">
                <i class="fas fa-trash-alt"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Upload Modal -->
    <div v-if="showUploadModal" class="modal-overlay" @click.self="showUploadModal = false">
      <div class="modal-container">
        <div class="modal-header">
          <h3>Upload Course File</h3>
          <button @click="showUploadModal = false" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="form-group">
            <label for="file-title">Title</label>
            <input 
              type="text" 
              id="file-title" 
              v-model="newFile.title" 
              class="form-input" 
              placeholder="Enter file title"
            >
          </div>
          
          <div class="form-group">
            <label>File</label>
            <div 
              class="file-upload-area"
              @click="triggerFileInput"
              @dragover.prevent="onDragOver"
              @dragleave.prevent="onDragLeave"
              @drop.prevent="onFileDrop"
              :class="{ 'dragging': isDragging }"
            >
              <input 
                type="file" 
                ref="fileInput" 
                @change="onFileSelected" 
                class="hidden-input"
              >
              
              <div v-if="!newFile.file" class="upload-placeholder">
                <i class="fas fa-cloud-upload-alt"></i>
                <p>Drag & drop a file here or click to browse</p>
              </div>
              
              <div v-else class="selected-file">
                <i class="fas fa-file"></i>
                <p>{{ newFile.file.name }}</p>
                <button @click.stop="removeSelectedFile" class="remove-file-btn">
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>
          
          <div v-if="uploadProgress > 0 && uploadProgress < 100" class="upload-progress">
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: `${uploadProgress}%` }"></div>
            </div>
            <p>{{ uploadProgress }}% Uploaded</p>
          </div>
          
          <div v-if="errorMessage" class="error-message">
            {{ errorMessage }}
          </div>
        </div>
        
        <div class="modal-footer">
          <button @click="showUploadModal = false" class="cancel-btn">Cancel</button>
          <button 
            @click="uploadFile" 
            class="upload-btn" 
            :disabled="!newFile.title || !newFile.file || uploading"
          >
            <span v-if="uploading">
              <i class="fas fa-spinner fa-spin"></i> Uploading...
            </span>
            <span v-else>Upload File</span>
          </button>
        </div>
      </div>
    </div>
    
    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click.self="showDeleteModal = false">
      <div class="modal-container delete-modal">
        <div class="modal-header">
          <h3>Delete File</h3>
          <button @click="showDeleteModal = false" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <p>Are you sure you want to delete the file "{{ fileToDelete?.title }}"?</p>
          <p class="warning">This action cannot be undone.</p>
        </div>
        
        <div class="modal-footer">
          <button @click="showDeleteModal = false" class="cancel-btn">Cancel</button>
          <button @click="deleteFile" class="delete-btn" :disabled="deleting">
            <span v-if="deleting">
              <i class="fas fa-spinner fa-spin"></i> Deleting...
            </span>
            <span v-else>Delete</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { ref, computed, onMounted } from 'vue';
import cloudflareR2Service from '@/services/cloudflareR2';

export default {
  name: 'CourseFiles',
  props: {
    courseId: {
      type: [Number, String],
      required: true
    },
    apiBaseUrl: {
      type: String,
      default: 'https://api.kewaar.com/api/admin/courses'
    },
    authToken: {
      type: String,
      required: true
    }
  },
  
  setup(props) {
    const files = ref([]);
    const loading = ref(true);
    const showUploadModal = ref(false);
    const showDeleteModal = ref(false);
    const fileToDelete = ref(null);
    const errorMessage = ref('');
    const uploading = ref(false);
    const deleting = ref(false);
    const uploadProgress = ref(0);
    const isDragging = ref(false);
    const fileInput = ref(null);
    
    const newFile = ref({
      title: '',
      file: null
    });
    
    // Check if Cloudflare R2 service is available
    const r2Configured = computed(() => {
      return cloudflareR2Service.isConfigured();
    });
    
    const apiUrl = computed(() => {
      return `${props.apiBaseUrl}/${props.courseId}/files`;
    });
    
    const headers = computed(() => {
      return {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${props.authToken}`
      };
    });
    
    // Fetch all course files
    const fetchFiles = async () => {
      loading.value = true;
      
      try {
        const response = await axios.get(apiUrl.value, {
          headers: headers.value
        });
        
        if (response.data && response.data.success) {
          files.value = response.data.data || [];
        } else {
          errorMessage.value = response.data.message || 'Failed to fetch files';
        }
      } catch (error) {
        console.error('Error fetching course files:', error);
        errorMessage.value = error.response?.data?.message || 'Failed to fetch files';
      } finally {
        loading.value = false;
      }
    };
    
    // File upload methods
    const openUploadModal = () => {
      newFile.value = {
        title: '',
        file: null
      };
      errorMessage.value = '';
      uploadProgress.value = 0;
      showUploadModal.value = true;
    };
    
    const triggerFileInput = () => {
      fileInput.value.click();
    };
    
    const onDragOver = () => {
      isDragging.value = true;
    };
    
    const onDragLeave = () => {
      isDragging.value = false;
    };
    
    const onFileDrop = (event) => {
      isDragging.value = false;
      if (event.dataTransfer.files.length > 0) {
        newFile.value.file = event.dataTransfer.files[0];
      }
    };
    
    const onFileSelected = (event) => {
      if (event.target.files.length > 0) {
        newFile.value.file = event.target.files[0];
      }
    };
    
    const removeSelectedFile = () => {
      newFile.value.file = null;
      if (fileInput.value) {
        fileInput.value.value = '';
      }
    };
    
    const uploadFile = async () => {
      if (!newFile.value.title || !newFile.value.file) {
        errorMessage.value = 'Please provide a title and select a file';
        return;
      }
      
      uploading.value = true;
      errorMessage.value = '';
      uploadProgress.value = 0;
      
      try {
        // If using Cloudflare R2
        if (r2Configured.value) {
          const uploadResponse = await cloudflareR2Service.uploadWithValidation(
            newFile.value.file,
            (percentage) => {
              uploadProgress.value = Math.round(percentage);
            }
          );
          
          // Now post the file metadata to your API
          const fileData = {
            title: newFile.value.title,
            file_url: uploadResponse.fileUrl
          };
          
          await postFileToApi(fileData);
        } else {
          // Direct upload to your API
          await uploadFileToApi();
        }
        
        // Refresh the files list
        await fetchFiles();
        
        // Close the modal
        showUploadModal.value = false;
      } catch (error) {
        console.error('Error uploading file:', error);
        errorMessage.value = error.message || 'Failed to upload file';
      } finally {
        uploading.value = false;
      }
    };
    
    const uploadFileToApi = async () => {
      const formData = new FormData();
      formData.append('title', newFile.value.title);
      formData.append('file', newFile.value.file);
      
      const uploadHeaders = {
        ...headers.value,
        'Content-Type': 'multipart/form-data'
      };
      
      await axios.post(apiUrl.value, formData, {
        headers: uploadHeaders,
        onUploadProgress: (progressEvent) => {
          uploadProgress.value = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
        }
      });
    };
    
    const postFileToApi = async (fileData) => {
      await axios.post(apiUrl.value, fileData, {
        headers: headers.value
      });
    };
    
    // File deletion methods
    const confirmDeleteFile = (file) => {
      fileToDelete.value = file;
      showDeleteModal.value = true;
    };
    
    const deleteFile = async () => {
      if (!fileToDelete.value) return;
      
      deleting.value = true;
      
      try {
        await axios.delete(`${apiUrl.value}/${fileToDelete.value.id}`, {
          headers: headers.value
        });
        
        // Remove from local list
        files.value = files.value.filter(f => f.id !== fileToDelete.value.id);
        
        // Close modal
        showDeleteModal.value = false;
      } catch (error) {
        console.error('Error deleting file:', error);
        errorMessage.value = error.response?.data?.message || 'Failed to delete file';
      } finally {
        deleting.value = false;
      }
    };
    
    // Helper methods
    const getFileUrl = (file) => {
      if (file.file_url.startsWith('http')) {
        return file.file_url;
      }
      return `https://api.kewaar.com/storage/${file.file_url}`;
    };
    
    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    };
    
    // Lifecycle hooks
    onMounted(() => {
      fetchFiles();
    });
    
    return {
      files,
      loading,
      showUploadModal,
      showDeleteModal,
      fileToDelete,
      errorMessage,
      uploading,
      deleting,
      uploadProgress,
      isDragging,
      fileInput,
      newFile,
      openUploadModal,
      triggerFileInput,
      onDragOver,
      onDragLeave,
      onFileDrop,
      onFileSelected,
      removeSelectedFile,
      uploadFile,
      confirmDeleteFile,
      deleteFile,
      getFileUrl,
      formatDate
    };
  }
};
</script>

<style scoped>
.course-files-container {
  margin-bottom: 2rem;
}

.card {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.card-header {
  padding: 1.25rem;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #333;
}

.card-content {
  padding: 1.25rem;
}

/* Button styles */
.add-file-btn {
  background-color: #4f46e5;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: background-color 0.2s;
}

.add-file-btn:hover {
  background-color: #4338ca;
}

.add-first-file-btn {
  background-color: #4f46e5;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-top: 1rem;
}

.add-first-file-btn:hover {
  background-color: #4338ca;
}

/* Loading state */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 0;
}

.spinner {
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 3px solid #4f46e5;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Empty state */
.empty-state {
  text-align: center;
  padding: 3rem 0;
  color: #666;
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #cbd5e1;
}

/* Files list */
.files-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.file-item {
  display: flex;
  align-items: center;
  padding: 1rem;
  border-radius: 6px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  transition: transform 0.2s, box-shadow 0.2s;
}

.file-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.file-icon {
  font-size: 1.5rem;
  color: #4f46e5;
  margin-right: 1rem;
}

.file-details {
  flex: 1;
}

.file-title {
  margin: 0 0 0.25rem 0;
  font-size: 1rem;
  color: #333;
}

.file-date {
  margin: 0;
  font-size: 0.875rem;
  color: #64748b;
}

.file-actions {
  display: flex;
  gap: 0.5rem;
}

.file-action-btn {
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  padding: 0.5rem;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.view-btn {
  color: #4f46e5;
  text-decoration: none;
}

.view-btn:hover {
  background-color: rgba(79, 70, 229, 0.1);
}

.delete-btn {
  color: #ef4444;
}

.delete-btn:hover {
  background-color: rgba(239, 68, 68, 0.1);
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-container {
  background-color: white;
  border-radius: 8px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow: auto;
}

.modal-header {
  padding: 1.25rem;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #333;
}

.close-btn {
  background-color: transparent;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  color: #64748b;
}

.modal-body {
  padding: 1.25rem;
}

.modal-footer {
  padding: 1.25rem;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

/* Form styles */
.form-group {
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
}

.form-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  font-size: 1rem;
}

.file-upload-area {
  border: 2px dashed #cbd5e1;
  border-radius: 4px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
}

.file-upload-area:hover,
.file-upload-area.dragging {
  border-color: #4f46e5;
  background-color: rgba(79, 70, 229, 0.05);
}

.hidden-input {
  display: none;
}

.upload-placeholder {
  color: #64748b;
}

.upload-placeholder i {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.selected-file {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #333;
}

.remove-file-btn {
  background-color: transparent;
  border: none;
  cursor: pointer;
  color: #ef4444;
  font-size: 1rem;
}

/* Progress bar */
.upload-progress {
  margin-top: 1rem;
}

.progress-bar {
  height: 8px;
  background-color: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.progress-fill {
  height: 100%;
  background-color: #4f46e5;
  transition: width 0.3s;
}

/* Message styles */
.error-message {
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 4px;
  color: #b91c1c;
}

.warning {
  color: #b91c1c;
  font-weight: 500;
}

/* Delete modal specific */
.delete-modal {
  max-width: 400px;
}

/* Button styles */
.cancel-btn {
  background-color: #e2e8f0;
  border: none;
  border-radius: 4px;
  padding: 0.75rem 1.25rem;
  font-size: 0.875rem;
  cursor: pointer;
  color: #333;
  transition: background-color 0.2s;
}

.cancel-btn:hover {
  background-color: #cbd5e1;
}

.upload-btn {
  background-color: #4f46e5;
  border: none;
  border-radius: 4px;
  padding: 0.75rem 1.25rem;
  font-size: 0.875rem;
  cursor: pointer;
  color: white;
  transition: background-color 0.2s;
}

.upload-btn:hover:not(:disabled) {
  background-color: #4338ca;
}

.upload-btn:disabled {
  background-color: #cbd5e1;
  cursor: not-allowed;
}
</style> 